"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useHeaderTheme } from "@/providers/HeaderTheme"
import { Logo } from "@/components/Logo/Logo"
import { HeaderNav } from "./Nav"
import { HeaderMobileNav } from "./Nav/MobileNav"
import { Menu, X } from "lucide-react"
import type { Header } from '@/payload-types'

interface HeaderClientProps { data: Header }

function HeaderClient({ data }: HeaderClientProps) {
  const [theme, setTheme] = useState<string | null>(null)
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { headerTheme, setHeaderTheme } = useHeaderTheme()
  const pathname = usePathname()

  useEffect(() => {
    setHeaderTheme(null)
    setMobileOpen(false) // close mobile on route change
  }, [pathname, setHeaderTheme])

  useEffect(() => {
    if (headerTheme && headerTheme !== theme) setTheme(headerTheme)
  }, [headerTheme, theme])

  return (
    <header id="site-header" className="relative z-80 w-full" {...(theme ? { "data-theme": theme } : {})}>
      <div className="w-full bg-transparent"> {/* full width */}
        <div className="mx-auto w-full max-w-screen-xl py-5 px-4 sm:px-6 flex items-center justify-between">
          {/* Left: logo (never shrink) */}
          <Link href="/" id="header-logo" className="text-black shrink-0">
            <Logo
              loading="eager"
              priority="high"
              className={isMegaMenuOpen ? "text-minigreen-1" : "text-minigreen-7 dark:text-minigreen-1"}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden min-[56rem]:flex">
            <HeaderNav
              data={data}
              className={isMegaMenuOpen ? "text-white" : "dark:invert-0"}
              onMegaMenuToggle={setIsMegaMenuOpen}
              isMegaMenuOpen={isMegaMenuOpen}
            />
          </div>

          {/* Mobile controls */}
          <div className="min-[56rem]:hidden flex items-center gap-3">
            <Link
              id="header-donate-button"
              href="https://secure.qgiv.com/for/mininaturereserve"
              className="rounded-full px-4 py-2 text-sm font-semibold tracking-wide bg-minigreen-7 text-white dark:bg-minigreen-2 dark:text-mini-primary"
            >
              DONATE
            </Link>

            <button
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex h-10 w-10 aspect-square items-center justify-center rounded-full border-2 border-minigreen-7/80 text-mini-black/80 dark:text-mini-white/80 dark:border-minigreen-2 shrink-0"
            >
              {mobileOpen ? <X size={20} strokeWidth={3} /> : <Menu size={20} strokeWidth={3} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      <HeaderMobileNav
        id="mobile-nav"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        data={data}
      />
    </header>
  )
}

export { HeaderClient }
export default HeaderClient
